<template>
  <div class="agent-service-window-host">
  <transition :name="dockable ? 'agent-service-dock-panel' : 'agent-service-window-fade'"
    @after-enter="finishDockMotion" @after-leave="focusDockBall">
    <section
      v-if="show"
      v-show="!isDocked"
      ref="panel"
      class="agent-service-window"
      :class="{ 'is-resizing': Boolean(resize), 'is-docking': isDocked, 'is-dockable': dockable }"
      :aria-hidden="isDocked ? 'true' : null"
      :inert="isDocked ? '' : null"
      role="dialog"
      aria-modal="false"
      :aria-label="resolvedLabel"
      :style="windowStyle"
      @pointerdown.capture="bringToFront"
      @keydown.esc.stop
    >
      <div class="window-grip-row">
        <!-- 移动聊天小窗，支持拖动与方向键 -->
        <div
          class="window-move-grip"
          role="button"
          tabindex="0"
          :aria-label="$t('agentServiceUI.moveGrip')"
          @pointerdown="startDrag"
          @pointermove="moveDrag"
          @pointerup="endDrag"
          @pointercancel="endDrag"
          @lostpointercapture="endDrag"
          @keydown="moveWithKeyboard"
        ></div>
      </div>
      <!-- 调节聊天窗口大小（{direction}），支持方向键 -->
      <div
        v-for="direction in resizeDirections"
        :key="direction"
        class="window-resize-handle"
        :class="`resize-${direction}`"
        role="button"
        tabindex="0"
        :aria-label="$t('agentServiceUI.resizeHandle', { direction })"
        @pointerdown.stop="startResize($event, direction)"
        @pointermove="moveResize"
        @pointerup="endResize"
        @pointercancel="endResize"
        @lostpointercapture="endResize"
        @keydown="resizeWithKeyboard($event, direction)"
      ></div>
      <!-- 聊天标题栏，可拖动或使用方向键移动窗口 -->
      <header
        ref="handle"
        class="chat-header" :class="{ 'has-compact-header': sidebarWidth > 0 && (size?.width || 480) < 560 }"
        tabindex="0"
        :aria-label="$t('agentServiceUI.headerGrip')"
        @pointerdown="startDrag"
        @pointermove="moveDrag"
        @pointerup="endDrag"
        @pointercancel="endDrag"
        @lostpointercapture="endDrag"
        @keydown="moveWithKeyboard"
      >
        <div v-if="$slots['header-sidebar'] || $scopedSlots['header-sidebar']" class="window-header-sidebar" :class="{ 'is-expanded': sidebarWidth > 0 }" :style="{ width: sidebarWidth + 'px' }" @pointerdown.stop @keydown.stop><slot name="header-sidebar" /></div>
        <div class="contact-profile">
          <div class="contact-avatar" aria-hidden="true">{{ initial }}</div>
          <div class="contact-copy">
            <div class="contact-title-row">
              <h2 :title="resolvedTitle">{{ resolvedTitle }}</h2>
              <slot name="title-badge" />
              <!-- 在线 -->
              <!-- 离线 -->
              <span
                v-if="showPresence"
                class="presence"
                :class="{ online: peerOnline }"
              >
                {{
                  peerOnline
                    ? $t("agentServiceUI.online")
                    : $t("agentServiceUI.offline")
                }}
              </span>
            </div>
            <span v-if="subtitle" class="scene-name">{{ subtitle }}</span>
          </div>
          <div class="profile-extra" @pointerdown.stop @keydown.stop><slot name="profile-extra" /></div>
        </div>
        <div class="header-actions" @pointerdown.stop @keydown.stop>
          <slot name="header-actions" />
          <button type="button" class="dock-button pin-button" :class="{ 'is-pinned': isPinned }"
            :aria-pressed="String(isPinned)" :title="$t(isPinned ? 'chatWorkbench.unpin' : 'chatWorkbench.pin')"
            :aria-label="$t(isPinned ? 'chatWorkbench.unpin' : 'chatWorkbench.pin')" @click.stop="togglePin">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 3 6 0-1 6 4 4v2H6v-2l4-4-1-6ZM12 15v6" /></svg>
          </button>
          <!-- 收纳会话到左侧 -->
          <!-- 收纳会话到右侧 -->
          <button v-if="dockable" type="button" class="dock-button"
            :title="$t(dockDirection === 'left' ? 'agentServiceUI.dockLeft' : 'agentServiceUI.dockRight')"
            :aria-label="$t(dockDirection === 'left' ? 'agentServiceUI.dockLeft' : 'agentServiceUI.dockRight')"
            @pointerdown.stop @click.stop="dockWindow">
            <svg viewBox="0 0 24 24" aria-hidden="true" :class="{ 'is-left': dockDirection === 'left' }">
              <path d="M19 4v16M4 12h11M10 7l5 5-5 5" />
            </svg>
          </button>
          <!-- 关闭聊天 -->
          <button
            type="button"
            class="close-button"
            :aria-label="$t('agentServiceUI.closeChat')"
            @pointerdown.stop
            @click="$emit('update:show', false)"
          >
            ×
          </button>
        </div>
      </header>
      <div class="window-body"><slot /></div>
    </section>
  </transition>
  <transition name="agent-service-dock-ball">
    <!-- 展开客服会话，可拖动或使用方向键移动悬浮球 -->
    <button v-if="show && isDocked" ref="dockBall" type="button" class="agent-service-dock-ball"
      :class="{ 'is-dragging': Boolean(ballDrag) }" :style="dockBallStyle"
      :aria-label="$t('agentServiceUI.restoreChat')" :title="$t('agentServiceUI.restoreChat')"
      aria-expanded="false" aria-haspopup="dialog"
      @pointerdown.stop="startBallDrag" @pointermove="moveBallDrag" @pointerup="endBallDrag"
      @pointercancel="endBallDrag" @lostpointercapture="endBallDrag"
      @click.stop="openDockBall" @keydown="moveBallWithKeyboard" @keydown.esc.stop>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13v-2a8 8 0 0 1 16 0v2M20 17v1a4 4 0 0 1-4 4h-3" /><rect x="2" y="11" width="4" height="7" rx="2" /><rect x="18" y="11" width="4" height="7" rx="2" /><path d="M11 22h3" /></svg>
    </button>
  </transition>
  </div>
</template>

<script>
import {
  AGENT_SERVICE_WINDOW_MIN_WIDTH,
  clampAgentServiceWindowPosition,
  agentServiceWindowSizeBounds,
  resizeAgentServiceWindow,
  resolveAgentServiceDockSide,
  clampAgentServiceDockBall,
  snapAgentServiceBall,
  dockAgentServiceBall,
  restoreAgentServiceDockWindow,
} from "@/services/agent-service-window-position";

// 两个窗口同时打开时，最后操作的窗口置顶，不互相关闭或共享业务会话。
let nextWindowLayer = 3000;

/** 共享的非模态聊天外壳，只管理位置、层级和主动关闭，不接管页面滚动或业务状态。 */
export default {
  name: "AgentServiceWindow",
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: "在线客服" },
    subtitle: { type: String, default: "" },
    initial: { type: String, default: "客" },
    label: { type: String, default: "客服聊天" },
    peerOnline: { type: Boolean, default: false },
    showPresence: { type: Boolean, default: true },
    sidebarWidth: { type: Number, default: 0 },
    dockable: { type: Boolean, default: true },
    docked: { type: Boolean, default: false },
    pinned: { type: Boolean, default: false },
  },
  data() {
    return {
      position: null,
      localDocked: false,
      localPinned: false,
      drag: null,
      resize: null,
      size: null,
      windowLayer: 3000,
      resizeDirections: ["n", "s", "e", "w", "ne", "nw", "se", "sw"],
      ballPosition: null,
      preDockRect: null,
      preDockFocus: null,
      ballWasMoved: false,
      ballDrag: null,
      suppressBallClick: false,
      lastDockSide: 'right',
      dockViewport: null,
      dockTransform: 'none',
      restoringDock: false,
    };
  },
  computed: {
    isDocked() { return this.dockable && (this.docked || this.localDocked); },
    isPinned() { return this.pinned || this.localPinned; },
    dockDirection() {
      const rect = { left: this.position?.x ?? this.preDockRect?.left ?? 0,
        width: this.size?.width ?? this.preDockRect?.width ?? 0 };
      const viewport = this.dockViewport;
      return viewport ? resolveAgentServiceDockSide(rect, viewport.viewportWidth, this.lastDockSide, viewport.offsetLeft) : 'right';
    },
    dockBallStyle() {
      return { '--dock-x': `${this.ballPosition?.x || 0}px`, '--dock-y': `${this.ballPosition?.y || 0}px`, zIndex: this.windowLayer };
    },
    resolvedTitle() {
      return this.title === "在线客服"
        // 在线客服
        ? this.$t("agentServiceUI.onlineSupport")
        : this.title;
    },
    resolvedLabel() {
      return this.label === "客服聊天"
        // 客服聊天
        ? this.$t("agentServiceUI.chatDialog")
        : this.label;
    },
    windowStyle() {
      return {
        "--chat-window-default-width": `${AGENT_SERVICE_WINDOW_MIN_WIDTH}px`,
        zIndex: this.windowLayer,
        ...(this.dockable ? { '--dock-panel-transform': this.dockTransform } : {}),
        ...(this.size
          ? { width: `${this.size.width}px`, height: `${this.size.height}px` }
          : {}),
        ...(this.position
          ? {
              left: `${this.position.x}px`,
              top: `${this.position.y}px`,
              right: "auto",
              bottom: "auto",
            }
          : {}),
      };
    },
  },
  watch: {
    pinned(value) { this.localPinned = value; },
    '$route.fullPath'(value, previous) {
      if (value !== previous && this.show && !this.isPinned && !this.isDocked) this.dockWindow();
    },
    show(value) {
      if (!value) {
        this.localDocked = false;
        this.endDrag();
        this.endBallDrag();
        this.restoringDock = false;
        return;
      }
      this.bringToFront();
      this.$nextTick(this.constrainPosition);
    },
    docked(value) {
      if (!this.dockable || !this.show) return;
      if (value) { this.endDrag(); this.$nextTick(this.focusDockBall); }
      else this.prepareDockRestore();
    },
  },
  mounted() {
    window.addEventListener("resize", this.constrainPosition);
    window.visualViewport?.addEventListener('resize', this.constrainPosition);
    window.visualViewport?.addEventListener('scroll', this.constrainPosition);
    if (this.show) {
      this.bringToFront();
      this.$nextTick(this.constrainPosition);
    }
  },
  beforeDestroy() {
    this.endDrag();
    this.endBallDrag();
    window.removeEventListener("resize", this.constrainPosition);
    window.visualViewport?.removeEventListener('resize', this.constrainPosition);
    window.visualViewport?.removeEventListener('scroll', this.constrainPosition);
  },
  methods: {
    /** 从未读提示进入时展开到视口允许的完整宽度，保留高度并沿用原拖拽/收纳边界。 */
    expandWidth() {
      if (!this.show || !this.$refs.panel) return;
      this.endDrag(); this.endBallDrag();
      const viewport = this.readDockViewport();
      const width = agentServiceWindowSizeBounds(viewport).maximumWidth;
      const height = Math.min(this.size?.height || this.$refs.panel.offsetHeight, Math.max(0, viewport.viewportHeight - 48));
      this.size = { width, height };
      this.position = { x: (viewport.offsetLeft || 0) + (viewport.viewportWidth - width) / 2,
        y: this.position?.y ?? (viewport.offsetTop || 0) + 32 };
      this.constrainPosition(); this.bringToFront();
    },
    togglePin() { const value = !this.isPinned; this.localPinned = value; this.$emit('update:pinned', value); },
    readDockViewport() {
      const visual = window.visualViewport;
      return { viewportWidth: visual?.width || window.innerWidth, viewportHeight: visual?.height || window.innerHeight,
        offsetLeft: visual?.offsetLeft || 0, offsetTop: visual?.offsetTop || 0 };
    },
    updateDockTransform(rect) {
      const ball = this.ballPosition;
      this.dockTransform = ball && rect?.width > 0 && rect?.height > 0
        ? `translate3d(${ball.x - rect.left}px, ${ball.y - rect.top}px, 0) scale(${48 / rect.width}, ${48 / rect.height})` : 'none';
    },
    dockWindow() {
      if (!this.show || !this.dockable || this.isDocked || !this.$refs.panel) return;
      this.endDrag();
      // 快速反向切换不能把动画途中缩小后的视觉矩形当成真实窗口尺寸。
      const rect = this.restoringDock && this.position && this.size
        ? { left: this.position.x, top: this.position.y, ...this.size }
        : this.$refs.panel.getBoundingClientRect();
      this.preDockRect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
      this.preDockFocus = typeof document !== 'undefined' && this.$refs.panel.contains(document.activeElement) ? document.activeElement : null;
      this.position = { x: rect.left, y: rect.top }; this.size = { width: rect.width, height: rect.height };
      this.dockViewport = this.readDockViewport();
      this.ballPosition = dockAgentServiceBall({ rect, previousSide: this.lastDockSide, ...this.dockViewport });
      this.lastDockSide = this.ballPosition.side; this.ballWasMoved = false; this.suppressBallClick = false;
      this.restoringDock = false;
      this.updateDockTransform(rect);
      if (!this.$listeners?.['update:docked']) this.localDocked = true;
      this.$emit('update:docked', true);
    },
    prepareDockRestore() {
      if (!this.preDockRect || !this.ballPosition) return;
      this.endBallDrag(); this.bringToFront();
      this.restoringDock = true;
      this.dockViewport = this.readDockViewport();
      const restored = restoreAgentServiceDockWindow({ rect: this.preDockRect, ball: this.ballPosition, moved: this.ballWasMoved, ...this.dockViewport });
      this.position = { x: restored.x, y: restored.y }; this.size = { width: restored.width, height: restored.height };
      this.updateDockTransform({ left: restored.x, top: restored.y, ...this.size });
    },
    openDockBall(event) {
      if (this.suppressBallClick && event?.detail !== 0) { this.suppressBallClick = false; return; }
      this.suppressBallClick = false;
      if (this.show && this.isDocked) {
        if (!this.$listeners?.['update:docked']) this.prepareDockRestore();
        this.localDocked = false; this.$emit('update:docked', false);
      }
    },
    focusDockBall() { if (this.show && this.isDocked) this.$refs.dockBall?.focus({ preventScroll: true }); },
    finishDockMotion() {
      if (!this.dockable || this.isDocked || !this.show || !this.restoringDock) return;
      this.restoringDock = false;
      const previous = this.preDockFocus;
      if (previous?.isConnected && this.$refs.panel?.contains(previous)) previous.focus({ preventScroll: true });
      else this.$refs.handle?.focus({ preventScroll: true });
    },
    startBallDrag(event) {
      if (!this.isDocked || this.ballDrag || event.isPrimary === false || (event.button !== undefined && event.button !== 0)) return;
      this.bringToFront(); this.suppressBallClick = false;
      this.ballDrag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY,
        origin: { ...this.ballPosition }, moved: false, captureTarget: event.currentTarget };
      event.currentTarget.setPointerCapture(event.pointerId);
      // 不preventDefault：轻点仍由原生click触发，触摸滚动由局部touch-action管理。
    },
    moveBallDrag(event) {
      const drag = this.ballDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (Math.hypot(dx, dy) > 6) drag.moved = true;
      if (!drag.moved) return;
      this.ballPosition = { ...clampAgentServiceDockBall({ x: drag.origin.x + dx, y: drag.origin.y + dy, ...this.readDockViewport() }), side: drag.origin.side };
    },
    endBallDrag(event) {
      const drag = this.ballDrag;
      if (!drag || (event?.pointerId != null && drag.pointerId !== event.pointerId)) return;
      this.ballDrag = null;
      if (event?.type === 'pointercancel' || !event) this.ballPosition = drag.origin;
      else if (drag.moved) {
        this.ballPosition = snapAgentServiceBall({ ...this.ballPosition, side: undefined, previousSide: this.lastDockSide, ...this.readDockViewport() });
        this.lastDockSide = this.ballPosition.side; this.ballWasMoved = true;
      }
      this.suppressBallClick = drag.moved || event?.type === 'pointercancel';
      if (drag.captureTarget.hasPointerCapture(drag.pointerId)) drag.captureTarget.releasePointerCapture(drag.pointerId);
    },
    moveBallWithKeyboard(event) {
      const delta = { ArrowUp: -16, ArrowDown: 16 }[event.key];
      const side = { ArrowLeft: 'left', ArrowRight: 'right', Home: 'left', End: 'right' }[event.key];
      if (delta == null && !side) return;
      event.preventDefault(); event.stopPropagation();
      this.ballPosition = snapAgentServiceBall({ ...this.ballPosition, y: this.ballPosition.y + (delta || 0), side: side || this.ballPosition.side, ...this.readDockViewport() });
      this.lastDockSide = this.ballPosition.side; this.ballWasMoved = true;
    },
    /** 置顶只影响浮窗，不给背景添加遮罩或禁止点击。 */
    bringToFront() {
      nextWindowLayer += 1;
      this.windowLayer = nextWindowLayer;
    },
    /** 鼠标和触摸共用Pointer捕获，关闭按钮不触发拖动。 */
    startDrag(event) {
      if (
        this.resize ||
        event.isPrimary === false ||
        (event.button !== undefined && event.button !== 0) ||
        event.target.closest?.("button, a, input, textarea")
      )
        return;
      const rect = this.$refs.panel.getBoundingClientRect();
      this.position = { x: rect.left, y: rect.top };
      this.drag = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        left: rect.left,
        top: rect.top,
        captureTarget: event.currentTarget || this.$refs.handle,
      };
      this.drag.captureTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    /** 限制拖动边界，消息区域自身仍按原逻辑滚动。 */
    moveDrag(event) {
      if (!this.drag || this.drag.pointerId !== event.pointerId) return;
      this.position = {
        x: this.drag.left + event.clientX - this.drag.x,
        y: this.drag.top + event.clientY - this.drag.y,
      };
      this.constrainPosition();
    },
    /** 主动关闭、取消拖动和销毁时释放捕获，不保留全局鼠标监听。 */
    endDrag() {
      const pointerId = this.drag?.pointerId;
      const handle = this.drag?.captureTarget;
      this.drag = null;
      if (pointerId !== undefined && handle?.hasPointerCapture(pointerId))
        handle.releasePointerCapture(pointerId);
      this.endResize();
    },
    /** 用边缘自身捕获Pointer，离开窄热区后仍连续缩放；不监听全局鼠标事件。 */
    startResize(event, direction) {
      if (
        this.drag ||
        this.resize ||
        event.isPrimary === false ||
        (event.button !== undefined && event.button !== 0)
      )
        return;
      this.resize = {
        pointerId: event.pointerId,
        direction,
        x: event.clientX,
        y: event.clientY,
        rect: this.$refs.panel.getBoundingClientRect(),
        captureTarget: event.currentTarget,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    /** 每次相对开始时的矩形计算，让宽高独立变化且避免连续舍入漂移。 */
    moveResize(event) {
      if (!this.resize || this.resize.pointerId !== event.pointerId) return;
      this.applyResize(
        this.resize.rect,
        this.resize.direction,
        event.clientX - this.resize.x,
        event.clientY - this.resize.y
      );
    },
    /** Pointer取消、主动关闭或销毁时释放缩放捕获；尺寸只保留在当前窗口实例。 */
    endResize() {
      const current = this.resize;
      this.resize = null;
      if (current?.captureTarget.hasPointerCapture(current.pointerId))
        current.captureTarget.releasePointerCapture(current.pointerId);
    },
    /** 应用纯几何结果；消息区由flex和既有布局观察自动适配，不重建业务会话。 */
    applyResize(rect, direction, dx, dy) {
      const result = resizeAgentServiceWindow({
        rect,
        direction,
        dx,
        dy,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      this.size = { width: result.width, height: result.height };
      this.position = { x: result.x, y: result.y };
    },
    /** 边缘获得键盘焦点后可用方向键缩放，Shift采用更大步长。 */
    resizeWithKeyboard(event, direction) {
      const step = event.shiftKey ? 48 : 16;
      const deltas = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const delta = deltas[event.key];
      if (!delta) return;
      event.preventDefault();
      event.stopPropagation();
      this.applyResize(
        this.$refs.panel.getBoundingClientRect(),
        direction,
        ...delta
      );
    },
    /** 改变视口或移动窗口后校正位置。 */
    constrainPosition() {
      if (this.dockable) {
        this.dockViewport = this.readDockViewport();
        if (this.isDocked && this.ballPosition) {
          this.ballPosition = snapAgentServiceBall({ ...this.ballPosition, ...this.dockViewport }); return;
        }
      }
      if (!this.$refs.panel) return;
      const rect = this.$refs.panel.getBoundingClientRect();
      const viewport = this.dockable ? this.dockViewport : { viewportWidth: window.innerWidth, viewportHeight: window.innerHeight };
      const offsetLeft = viewport.offsetLeft || 0, offsetTop = viewport.offsetTop || 0;
      if (!this.position) this.position = { x: rect.left, y: rect.top };
      if (this.size) {
        const bounds = agentServiceWindowSizeBounds(viewport);
        const width = Math.max(
          bounds.minimumWidth,
          Math.min(this.size.width, bounds.maximumWidth)
        );
        this.size = { width, height: Math.min(this.size.height, Math.max(0, viewport.viewportHeight - 48)) };
      }
      const clamped = clampAgentServiceWindowPosition({
        x: this.position.x - offsetLeft, y: this.position.y - offsetTop,
        width: this.size?.width || rect.width,
        height: this.size?.height || rect.height,
        ...viewport,
      });
      this.position = { x: clamped.x + offsetLeft, y: clamped.y + offsetTop };
      // 保留顶部安全留白；抓手位于窗口最顶部，不占用窗口外空间。
      this.position.y = Math.max(offsetTop + 32, this.position.y);
      if (this.dockable) {
        this.preDockRect = { left: this.position.x, top: this.position.y, width: this.size?.width || rect.width, height: this.size?.height || rect.height };
        this.lastDockSide = resolveAgentServiceDockSide(this.preDockRect, this.dockViewport.viewportWidth, this.lastDockSide, this.dockViewport.offsetLeft);
      }
    },
    /** 标题栏获得焦点后，方向键是拖动的无障碍替代操作。 */
    moveWithKeyboard(event) {
      const offsets = {
        ArrowLeft: [-16, 0],
        ArrowRight: [16, 0],
        ArrowUp: [0, -16],
        ArrowDown: [0, 16],
      };
      const offset = offsets[event.key];
      if (
        (event.target !== this.$refs.handle &&
          !event.target.classList?.contains("window-move-grip")) ||
        !offset
      )
        return;
      event.preventDefault();
      const rect = this.$refs.panel.getBoundingClientRect();
      this.position = { x: rect.left + offset[0], y: rect.top + offset[1] };
      this.constrainPosition();
    },
  },
};
</script>

<style lang="less" scoped>
.agent-service-window-host { display: contents; }
.agent-service-window {
  --chat-radius: 12px;
  position: fixed;
  right: 24px;
  bottom: max(24px, env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  width: min(var(--chat-window-default-width, 480px), calc(100vw - 32px));
  height: min(720px, calc(100vh - 48px - env(safe-area-inset-bottom)));
  height: min(720px, calc(100dvh - 48px - env(safe-area-inset-bottom)));
  aspect-ratio: auto;
  box-sizing: border-box;
  // 缩放热区越过边框，内容裁剪放在body。
  overflow: visible;
  border: 1px solid #dde3ee;
  border-radius: 12px;
  background: #f5f5f5;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
  &.is-dockable {
    transform-origin: top left;
    .window-body { transition: opacity 100ms ease; }
    &.is-docking {
      pointer-events: none;
      .window-body { opacity: 0; }
    }
  }

  .dock-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: #667085;
    background: transparent;
    cursor: pointer;
    transition: color 200ms ease, background-color 200ms ease;
    svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; &.is-left { transform: rotate(180deg); } }
    &:hover { color: #246de0; background: #f2f7ff; }
    &:focus-visible { outline: 2px solid #4286f5; outline-offset: -2px; }
    &.is-pinned { color: #246de0; svg { filter: drop-shadow(0 1px 1px rgba(36,109,224,.18)); } }
  }
  &.is-resizing {
    border-color: var(--m-brand-500, #4286f5);
    user-select: none;
  }
  .window-grip-row {
    position: relative;
    z-index: 4;
    height: 20px;
    flex: 0 0 20px;
    border-bottom: 1px solid #eef2f7;
    border-radius: 12px 12px 0 0;
    background: #fff;
  }
  .window-move-grip {
    position: absolute;
    top: 2px;
    left: calc(50% - 44px);
    width: 88px;
    z-index: 4;
    height: 18px;
    cursor: grab;
    touch-action: none;
    &::after {
      content: "";
      position: absolute;
      top: 2px;
      left: calc(50% - 18px);
      width: 36px;
      height: 4px;
      border-radius: 999px;
      background: var(--m-text-secondary, #667085);
      transition: background-color 120ms;
    }
    &:hover::after,
    &:focus-visible::after {
      background: var(--m-brand-500, #4286f5);
    }
    &:active {
      cursor: grabbing;
    }
    &:focus-visible {
      outline: 2px solid var(--m-brand-500, #4286f5);
    }
    @media (prefers-reduced-motion: reduce) {
      &::after {
        transition: none;
      }
    }
  }
  .window-resize-handle {
    position: absolute;
    z-index: 3;
    touch-action: none;
    &:focus-visible {
      outline: 2px solid var(--m-brand-500, #4286f5);
    }
    &.resize-n,
    &.resize-s {
      height: 20px;
      left: 12px;
      right: 12px;
      cursor: ns-resize;
    }
    // 顶部和右边留开关闭按钮热区，不能拦截其点击。
    &.resize-n {
      top: -10px;
      right: 76px;
    }
    &.resize-s {
      bottom: -10px;
    }
    &.resize-e,
    &.resize-w {
      width: 20px;
      top: 12px;
      bottom: 12px;
      cursor: ew-resize;
    }
    &.resize-e {
      right: -10px;
      top: 68px;
    }
    &.resize-w {
      left: -10px;
    }
    &.resize-ne,
    &.resize-nw,
    &.resize-se,
    &.resize-sw {
      width: 24px;
      height: 24px;
    }
    &.resize-ne {
      top: -12px;
      right: -12px;
      cursor: nesw-resize;
    }
    &.resize-nw {
      top: -12px;
      left: -12px;
      cursor: nwse-resize;
    }
    &.resize-se {
      bottom: -12px;
      right: -12px;
      cursor: nwse-resize;
    }
    &.resize-sw {
      bottom: -12px;
      left: -12px;
      cursor: nesw-resize;
    }
  }
  .chat-header {
    position: relative;
    z-index: 20;
    border-radius: 0 0 var(--chat-radius, 12px) var(--chat-radius, 12px);
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    min-height: 64px;
    padding: 8px 12px;
    border-bottom: 1px solid #eef2f7;
    background: #fff;
    color: #333;
    touch-action: auto;
    user-select: none;
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
    &:focus-visible {
      outline: 2px solid #4286f5;
      outline-offset: -2px;
    }
    .contact-avatar,
    .header-actions .dock-button,
    .header-actions .close-button {
      transition: width 240ms cubic-bezier(.2,.8,.2,1), height 240ms cubic-bezier(.2,.8,.2,1), flex-basis 240ms cubic-bezier(.2,.8,.2,1), background-color 160ms ease, color 160ms ease;
    }
    .profile-extra ::v-deep .agent-service-language,
    .profile-extra ::v-deep .language-select {
      transition: width 240ms cubic-bezier(.2,.8,.2,1), height 240ms cubic-bezier(.2,.8,.2,1), min-width 240ms cubic-bezier(.2,.8,.2,1), min-height 240ms cubic-bezier(.2,.8,.2,1), flex-basis 240ms cubic-bezier(.2,.8,.2,1), color 160ms ease;
    }
    @media (prefers-reduced-motion: reduce) {
      .contact-avatar, .header-actions .dock-button, .header-actions .close-button,
      .profile-extra ::v-deep .agent-service-language, .profile-extra ::v-deep .language-select { transition: none; }
    }
    &.has-compact-header {
      .contact-profile { gap: 4px; }
      .contact-avatar { width: 32px; height: 32px; flex-basis: 32px; }
      .header-actions { .dock-button, .close-button { width: 32px; height: 32px; flex: 0 0 32px; } }
      .profile-extra ::v-deep .agent-service-language,
      .profile-extra ::v-deep .language-select { width: 32px; min-width: 32px; height: 32px; min-height: 32px; flex-basis: 32px; }
    }
    .window-header-sidebar { flex: 0 0 auto; align-self: stretch; display: flex; align-items: center; box-sizing: border-box; overflow: hidden; margin-left: -12px; margin-right: 12px; cursor: default; transition: width 240ms cubic-bezier(.2,.8,.2,1);
      > * { flex: 0 0 260px; transform: translate3d(-260px,0,0); transition: transform 240ms cubic-bezier(.2,.8,.2,1); }
      &.is-expanded > * { transform: translate3d(0,0,0); }
      @media (prefers-reduced-motion: reduce) { &, > * { transition: none; } }
    }
    .profile-extra { flex: 0 0 auto; }
    .contact-profile {
      display: flex;
      flex: 1;
      min-width: 0;
      align-items: center;
      gap: 8px;
      transition: gap 240ms cubic-bezier(.2,.8,.2,1);
      @media (prefers-reduced-motion: reduce) { transition: none; }
      touch-action: none;
    }
    .header-actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 0;
      cursor: default;
    }
    .contact-avatar {
      display: flex;
      width: 40px;
      height: 40px;
      flex: 0 0 40px;
      align-items: center;
      justify-content: center;
      border-radius: var(--chat-radius, 12px);
      background: #07c160;
      color: #fff;
      font-size: 17px;
      font-weight: 600;
    }
    .contact-copy {
      min-width: 0;
    }
    .contact-title-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    h2 {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 16px;
      font-weight: 600;
    }
    .presence {
      flex: 0 0 auto;
      color: #98a2b3;
      font-size: 12px;
      font-weight: 500;
      &.online {
        color: #246de0;
      }
    }
    .scene-name {
      display: block;
      max-width: 300px;
      margin-top: 4px;
      overflow: hidden;
      color: #667085;
      font-size: 12px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .close-button {
      width: 44px;
      height: 44px;
      flex: 0 0 44px;
      border: 0;
      border-radius: var(--chat-radius, 12px);
      background: transparent;
      color: #667085;
      font-size: 26px;
      line-height: 1;
      cursor: pointer;
      &:hover {
        background: #f2f7ff;
        color: #246de0;
      }
      &:focus-visible {
        outline: 2px solid #4286f5;
      }
    }
  }
  .window-body {
    position: relative;
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    overflow: hidden;
    border-radius: 0 0 12px 12px;
  }
  @media (max-width: 600px) {
    right: 16px;
    bottom: max(16px, env(safe-area-inset-bottom));
    width: calc(100vw - 32px);
  }
}
.agent-service-window-fade-enter-active,
.agent-service-window-fade-leave-active {
  transition: opacity 200ms ease;
}
.agent-service-window-fade-enter,
.agent-service-window-fade-leave-to {
  opacity: 0;
}
.agent-service-dock-panel-enter-active,
.agent-service-dock-panel-leave-active {
  will-change: transform, opacity;
  transition: transform 220ms cubic-bezier(.2,.8,.2,1), opacity 220ms cubic-bezier(.2,.8,.2,1);
  pointer-events: none;
}
.agent-service-dock-panel-enter,
.agent-service-dock-panel-leave-to {
  transform: var(--dock-panel-transform, none);
  opacity: 0;
}
.agent-service-window.agent-service-dock-panel-enter .window-body { opacity: 0; }
.agent-service-window.agent-service-dock-panel-enter-active .window-body,
.agent-service-window.agent-service-dock-panel-leave-active .window-body { transition: opacity 220ms cubic-bezier(.2,.8,.2,1); }
.agent-service-dock-ball {
  position: fixed;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #c7dcfd;
  border-radius: 50%;
  background: #fff;
  color: #246de0;
  box-shadow: 0 8px 24px rgba(15,23,42,.12);
  transform: translate3d(var(--dock-x), var(--dock-y), 0) scale(var(--dock-ball-scale, 1));
  touch-action: none;
  user-select: none;
  cursor: grab;
  transition: transform 200ms cubic-bezier(.2,.8,.2,1), opacity 200ms ease, background-color 200ms ease;
  svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; pointer-events: none; }
  &:hover { background: #f2f7ff; }
  &:focus-visible { outline: 2px solid #4286f5; outline-offset: 2px; }
  &.is-dragging { transition: none; cursor: grabbing; }
}
.agent-service-dock-ball-enter-active,
.agent-service-dock-ball-leave-active { transition-duration: 220ms; }
.agent-service-dock-ball-enter,
.agent-service-dock-ball-leave-to { --dock-ball-scale: .75; opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .agent-service-dock-panel-enter-active,
  .agent-service-dock-panel-leave-active,
  .agent-service-dock-ball,
  .agent-service-window.is-dockable .window-body,
  .agent-service-window .dock-button,
  .agent-service-window-fade-enter-active,
  .agent-service-window-fade-leave-active {
    transition: none;
  }
}
</style>
